'use memo'

import { useSelector } from 'src/hooks/useRedux'
import {
  getGenderTypes,
  getGenericSportById,
  getPlayerTypesBySportId,
  getSportById,
} from '@sportninja/common/selectors/types'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import type { CompetitionResult } from '@sportninja/common/features/teamFinder/types'
import {
  toTeamFinderRow,
  type TeamFinderRow,
} from '@sportninja/common/features/teamFinder/resultRow'

const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

interface GenderType {
  id: string | number
  name?: string
  name_full?: string
}

interface PlayerType {
  id: string | number
  name?: string
  name_full?: string
}

interface SportRecord {
  name?: string | null
  name_full?: string | null
}

/**
 * Resolve EVERY result to a flat table row in one pass.
 *
 * The card's `useResultLabels` / `useResultSport` are per-result hooks, which
 * cannot be called in a loop — hence this bulk sibling rather than a reuse. It
 * takes a single `useSelector` over the type stores and resolves each result
 * inside it, so the work is one subscription for the whole table instead of
 * two per row.
 *
 * The card is deliberately left on its own hooks: it renders one result and
 * needs the banner artwork too, which no table column shows.
 */
export const useResultRows = (results: CompetitionResult[]): TeamFinderRow[] =>
  useSelector<TeamFinderRow[]>((state) => {
    const genderTypes = (getGenderTypes(state) ?? []) as GenderType[]

    return results.map((result) => {
      const sport = getSportById(state, result.sportId) as SportRecord | false
      const genericSport = getGenericSportById(
        state,
        result.genericSportId
      ) as SportRecord | null

      // A generic (basic-stats) sport is the more specific of the two — a
      // "Ball Hockey" listing carries it while `sportId` points at the
      // full-stats parent — so it wins the label.
      const sportName =
        genericSport?.name_full ||
        genericSport?.name ||
        (sport ? sport.name_full || sport.name : undefined) ||
        undefined

      const genderMatch =
        result.genderTypeId == null
          ? undefined
          : genderTypes.find(
              (g) => String(g.id) === String(result.genderTypeId)
            )

      // Positions resolve against the result's OWN sport: the same numeric id
      // means different things across sports, so a global lookup would label a
      // soccer opening with a hockey position.
      const playerTypes = (getPlayerTypesBySportId(state, result.sportId) ??
        []) as PlayerType[]
      const positionById = new Map(
        playerTypes.map((p) => [
          String(p.id),
          p.name_full ?? p.name ?? String(p.id),
        ])
      )
      const positionNames = (result.positionsNeeded ?? [])
        .map((id) => positionById.get(String(id)))
        .filter((name): name is string => Boolean(name))

      return toTeamFinderRow(result, {
        sportName,
        genderLabel: genderMatch?.name_full ?? genderMatch?.name ?? undefined,
        positionNames,
        teamLogo: resolveAssetUrl(result.teamImage) ?? TEAM_PLACEHOLDER,
      })
    })
  })
